import cx from 'classnames';
import { useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Arrow } from '../images/chevron_down_small.svg';
import styles from '../sass/components/ProductCard.module.scss';
import Badge from './Badge';
import Button from './Button';
import ModalCloseButton from './ModalCloseButton';
import { ProductCardProps } from '../interfaces/Components';
import info2 from '../images/info2.svg';
import { ServiceType } from '../interfaces/WorkOrder';
import { useTenant } from './TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
import { formatMoney } from '../i18n/format';

/** A details line that only makes sense where the "More Info" button is. */
const isTouchHint = (line: string) => /^\s*tap more info\b/i.test(line);

function ProductCard({
  title,
  price,
  isEstimate,
  subDescription,
  details,
  onSelect,
  selected,
  onRemove,
  disabled,
  discountedPrice,
  serviceTypeName,
  fromPrice,
  fromPriceLabel,
  subscriptionPlan,
  bestValue = false,
  mobileServiceFeeCents = null,
  mobileServiceFeeHint = null,
  mobileServiceFeeIncludedFirstVisit = false,
  titleLang,
  detailsLang,
}: ProductCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  // Class hooks for the Aperio preset's stylesheet (readable plan terms, no
  // "Tap More Info" where there is no More Info button). Absent for every
  // other tenant, whose class lists are unchanged.
  const themed = normalizeThemePreset(useTenant()?.themePreset) !== 'default';

  function renderWithSmall(text: string, smallClass: string): ReactNode {
    const src = text ?? '';
    const match = /\[small\]([\s\S]*?)\[\/small\]/i.exec(src);
    if (!match) return src; // string is a valid ReactNode

    const before = src.slice(0, match.index!);
    const inner = match[1];
    const after = src.slice(match.index! + match[0].length);

    return (
      <>
        {before}
        <span className={smallClass}>{inner}</span>
        {renderWithSmall(after, smallClass)}
      </>
    );
  }

  const getProductFinalPrice = () => {
    if (discountedPrice === 0) {
      return t('product.included');
    }
    const amount = formatMoney(discountedPrice || price);
    return isEstimate ? t('product.fromPrice', { price: amount }) : amount;
  };

  const productOriginalPrice = Number.isFinite(discountedPrice)
  && discountedPrice !== price
    ? t('product.originalPrice', { price: formatMoney(price) })
    : null;

  const showFromPrice = fromPrice !== null
    && fromPrice !== undefined
    && discountedPrice !== 0
    && fromPrice < (discountedPrice ?? price);

  /** Airline-style "from $X" block for one-time services. */
  const renderPrice = () => {
    if (!showFromPrice) {
      return getProductFinalPrice();
    }
    return (
      <div className={styles.fromPriceBlock}>
        <span className={styles.fromPrice}>
          {t('product.fromPrice', { price: formatMoney(fromPrice as number) })}
        </span>
        <span className={styles.fromPriceHint}>
          {fromPriceLabel || t('product.bestPriceAtTimes')}
          {' · '}
          {t('product.standardPrice', {
            price: formatMoney(discountedPrice ?? price),
          })}
        </span>
      </div>
    );
  };

  /**
   * Subscription cards: make plan price, monthly equivalent and the one-time
   * onboarding fee explicit instead of a single confusing number.
   */
  const renderSubscriptionPrice = () => {
    if (!subscriptionPlan) {
      return getProductFinalPrice();
    }
    const plan = subscriptionPlan.price;
    const onboarding = discountedPrice ?? price;
    const isAnnual = subscriptionPlan.frequency === 'annual';
    return (
      <div className={styles.planPriceBlock}>
        <span className={styles.planPrice}>
          {formatMoney(plan)}
          <span className={styles.planPer}>
            {isAnnual ? t('plans.per.year') : t('plans.per.month')}
          </span>
        </span>
        {isAnnual && (
          <span className={styles.planHint}>
            {t('product.perMonthUpfront', { price: formatMoney(plan / 12) })}
          </span>
        )}
        {!isAnnual && (
          <span className={styles.planHint}>{t('product.billedMonthly')}</span>
        )}
        {subscriptionPlan.priceNote && (
          <span className={styles.planHint}>
            {subscriptionPlan.priceNote}
          </span>
        )}
        <span className={styles.planHint}>
          {t('product.plusOnboarding', { price: formatMoney(onboarding) })}
        </span>
        {mobileServiceFeeCents !== null && mobileServiceFeeCents > 0 && (
          <span className={styles.planHint}>
            +
            {' '}
            {mobileServiceFeeHint
              || t('product.feePerVisit', {
                price: formatMoney(mobileServiceFeeCents / 100),
              })}
          </span>
        )}
        {mobileServiceFeeIncludedFirstVisit && !!mobileServiceFeeCents && (
          <span className={styles.planHint}>
            {t('product.feeIncludedFirstVisit')}
          </span>
        )}
        <span className={styles.planTotal}>
          {t('product.firstVisitTotal', {
            price: formatMoney(
              plan + onboarding + (mobileServiceFeeIncludedFirstVisit
                ? 0
                : (mobileServiceFeeCents ?? 0) / 100),
            ),
          })}
        </span>
      </div>
    );
  };

  if (serviceTypeName === ServiceType.subscription) {
    return (
      <div className={styles.cardContainerSubscribtion}>
        {bestValue && (
          <Badge variant="tertiary"><span>{t('product.bestValue')}</span></Badge>
        )}
        <div className={styles.mainInfoContainer}>
          <div className={cx([
            styles.leftInfoContainer,
            { [styles.expanded]: expanded },
          ])}
          >
            <Button
              type="button"
              className={styles.arrowButton}
              variant="icon"
              onClick={() => setExpanded(!expanded)}
              inactive={disabled}
              aria-label={t('product.toggleDetails', { title })}
            >
              <span className={styles.arrowCircle}>
                <Arrow className={cx(styles.dropdownChevron,
                  { [styles.expandedArrow]: expanded })}
                />
              </span>
            </Button>
          </div>
          <div className={styles.description}>
            <div className={styles.titleWithIcon}>
              <h4 lang={titleLang}>{title}</h4>
            </div>
            {showInfo && (
              <div className={styles.modalOverlay}>
                <div className={styles.modalContent}>
                  <ModalCloseButton onClose={() => setShowInfo(false)} />
                  <p className={styles.bodyText}>
                    {renderWithSmall(
                      subDescription ?? '',
                      cx(styles.smallText, { 'apr-small-print': themed }),
                    )}
                  </p>
                  <div className={styles.modalActions}>
                    <Button
                      variant="primary"
                      onClick={() => setShowInfo(false)}
                      className={styles.closeButton}
                    >
                      {t('common.close')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.rightInfoContainer}>
            {renderSubscriptionPrice()}
            {productOriginalPrice && !subscriptionPlan && (
              <p className={styles.originalPrice}>{productOriginalPrice}</p>
            )}
            <div className={styles.rightAddServiceToggle}>
              {selected && (
                <Button
                  className={styles.selectButton}
                  onClick={onRemove}
                  variant="tertiary"
                  error
                  inactive={disabled}
                >
                  {t('product.remove')}
                </Button>
              )}
              <Button
                variant="primary"
                onClick={onSelect}
                inactive={selected || disabled}
              >
                {selected ? t('product.selected') : t('product.select')}
              </Button>
            </div>
          </div>
        </div>
        <div className={styles.mobileButtons}>
          <div className={styles.mobileSelectButtons}>
            {selected && (
              <Button
                onClick={onRemove}
                variant="tertiary"
                error
                inactive={disabled}
              >
                {t('product.remove')}
              </Button>
            )}
            <Button
              variant="primary"
              onClick={onSelect}
              inactive={selected || disabled}
            >
              {selected ? t('product.selected') : t('product.select')}
            </Button>
          </div>
          <Button
            variant="secondary"
            onClick={() => setExpanded(!expanded)}
            inactive={disabled}
          >
            {expanded ? t('product.lessInfo') : t('product.moreInfo')}
          </Button>
        </div>
        <div className={cx(
          { [styles.containerExpanded]: expanded },
          styles.expandedInfoContainerSubscribtion,
        )}
        >
          <div className={styles.detailsCopy} lang={detailsLang}>
            {details && details.split('\\n').map((item) => (
              <span
                key={item}
                className={themed && isTouchHint(item)
                  ? 'apr-touch-hint' : undefined}
              >
                {item}
                <br />
              </span>
            ))}
          </div>
          <Button
            variant="icon"
            onClick={() => setShowInfo(true)}
            className={styles.infoButton}
            aria-label={t('product.info')}
          >
            <img src={info2} alt={t('product.info')} />
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.cardContainer}>
      <div className={styles.mainInfoContainer}>
        <div className={cx([
          styles.leftInfoContainer,
          { [styles.expanded]: expanded },
        ])}
        >
          <Button
            type="button"
            className={styles.arrowButton}
            variant="icon"
            onClick={() => setExpanded(!expanded)}
            inactive={disabled}
            aria-label={t('product.toggleDetails', { title })}
          >
            <span className={styles.arrowCircle}>
              <Arrow className={cx(styles.dropdownChevron,
                { [styles.expandedArrow]: expanded })}
              />
            </span>
          </Button>
        </div>
        <div className={styles.description}>
          <h4 lang={titleLang}>{title}</h4>
        </div>
        <div className={styles.rightInfoContainer}>
          {renderPrice()}
          {productOriginalPrice && (
          <p className={styles.originalPrice}>{productOriginalPrice}</p>
          )}
          <div className={styles.rightAddServiceToggle}>
            {selected && (
            <Button
              className={styles.selectButton}
              onClick={onRemove}
              variant="tertiary"
              error
              inactive={disabled}
            >
              {t('product.remove')}
            </Button>
            )}
            <Button
              variant="primary"
              onClick={onSelect}
              inactive={selected || disabled}
            >
              {selected ? t('product.selected') : t('product.select')}
            </Button>
          </div>
        </div>
      </div>
      <div className={styles.mobileButtons}>
        <div className={styles.mobileSelectButtons}>
          {selected && (
          <Button
            onClick={onRemove}
            variant="tertiary"
            error
            inactive={disabled}
          >
            {t('product.remove')}
          </Button>
          )}
          <Button
            variant="primary"
            onClick={onSelect}
            inactive={selected || disabled}
          >
            {selected ? t('product.selected') : t('product.select')}
          </Button>
        </div>
        <Button
          variant="secondary"
          onClick={() => setExpanded(!expanded)}
          inactive={disabled}
        >
          {expanded ? t('product.lessInfo') : t('product.moreInfo')}
        </Button>
      </div>
      <div className={cx(
        { [styles.containerExpanded]: expanded },
        styles.expandedInfoContainer,
      )}
      >
        <div className={styles.detailsCopy} lang={detailsLang}>
          {details && details.split('\\n').map((item) => (
            <span key={item}>
              {item}
              <br />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ProductCard;
